import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import EventCard from "./EventCard";
import EventFilters from "./EventFilters";
import ResultDisplay from "./ResultDisplay";
import useEvents from "./useEvents";
import "./EventsTab.css";
import { useAuth } from "../../../../../context/AuthContext";

/**
 * EventsTab is the main container component for the Events tab
 * It manages loading, filtering and displaying social events
 *
 * Props:
 *   canEdit  -- when false (regular users on the top-level Calendar
 *               tab), the create-event button, post-to-calendar
 *               button, and per-card Select control are hidden. The
 *               same component is reused inside the Captains parent
 *               where canEdit is always true.
 *
 * @returns {JSX.Element}
 */
const EventsTab = ({ canEdit = false }) => {
  // Get auth context
  const { getAuthHeaders, logout } = useAuth();
  const navigate = useNavigate();

  // Use our custom hook for events state and functionality
  const {
    socialEvents,
    selectedEvents,
    daysAhead,
    calendarUpdateResults,
    expandedEvents,
    loading,
    errorMessage,
    successMessage,
    loadSocialEvents,
    handleDaysAheadChange,
    handleSelectEvent,
    handleSubmitCalendarEvents,
    toggleEventExpansion,
  } = useEvents({ getAuthHeaders, logout });

  // Load events when component mounts
  useEffect(() => {
    loadSocialEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load events when days ahead changes
  useEffect(() => {
    loadSocialEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysAhead]);

  return (
    <div className="events-tab">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <h2 className="events-title" style={{ margin: 0 }}>
          {canEdit ? "Manage Social Events" : "Upcoming Events"}
        </h2>
        {canEdit && (
          <button
            type="button"
            onClick={() => navigate("/admin/create-event")}
            style={{
              padding: "8px 14px",
              background: "#4a89dc",
              color: "white",
              border: "none",
              borderRadius: 6,
              fontSize: 14,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            + New Event
          </button>
        )}
      </div>

      {/* Event Filters */}
      <EventFilters
        daysAhead={daysAhead}
        onDaysAheadChange={handleDaysAheadChange}
        onSubmitToCalendar={handleSubmitCalendarEvents}
        onRefresh={loadSocialEvents}
        selectedCount={Object.keys(selectedEvents).length}
        loading={loading}
        canPost={canEdit}
      />

      {/* Status Messages */}
      {errorMessage && <div className="error-message">{errorMessage}</div>}
      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}
      {loading && <div className="loading-spinner">Loading...</div>}

      {/* Events List */}
      {socialEvents.length === 0 && !loading ? (
        <div className="no-data">No upcoming social events found</div>
      ) : (
        <div className="events-list">
          {socialEvents.map((event) => (
            <EventCard
              key={event.socialEventId}
              event={event}
              isSelected={!!selectedEvents[event.socialEventId]}
              isExpanded={!!expandedEvents[event.socialEventId]}
              onSelect={() => handleSelectEvent(event.socialEventId)}
              onToggleExpand={() => toggleEventExpansion(event.socialEventId)}
              canSelect={canEdit}
            />
          ))}
        </div>
      )}

      {/* Calendar Update Results */}
      <ResultDisplay results={calendarUpdateResults} />
    </div>
  );
};

export default EventsTab;
